import { useState } from 'react';
import type { Me } from '../lib/api';

/**
 * UCCX → WxCC Flow Convertor — upload a UCCX .aef and get a WxCC-importable
 * flow JSON back, via uccx-flow-to-wxcc-svc (gated module 'uccx_flow_convertor').
 */
const API = (import.meta as any).env?.VITE_API_BASE ?? 'https://api.trinethub.com';

export default function UccxConvert({ me }: { me: Me }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState('');

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(''); setRes(null); setBusy(true);
    try {
      const buf = await f.arrayBuffer();
      let bin = ''; const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = btoa(bin);
      const flowName = (name || f.name.replace(/\.aef$/i, '')).replace(/[^\w]+/g, '_');
      const r = await fetch(`${API}/api/uccx-convert/convert`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: flowName, aef_base64: b64 }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      setRes(j);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  function download() {
    if (!res?.flow) return;
    const blob = new Blob([JSON.stringify(res.flow, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `${res.flow.name || 'flow'}.wxcc.json`; a.click();
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">UCCX → WxCC Flow Convertor</h1>
        <p className="text-sm text-gray-500">Upload a UCCX <code>.aef</code> script — get a WxCC-importable flow JSON (flow-local variables, MANUAL_REVIEW comments for unmapped steps). tenant {me.tenant_id}</p>
      </div>

      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-5 space-y-3">
        <label className="block text-sm">
          <span className="text-gray-500">Flow name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Utilities_2300_Flow"
            className="mt-1 block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-500">UCCX .aef file</span>
          <input type="file" accept=".aef" onChange={onFile} disabled={busy}
            className="mt-1 block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-tnh-accent file:text-white file:text-xs hover:file:bg-tnh-accent/90" />
        </label>
        {busy && <div className="text-sm text-gray-500 animate-pulse">Converting…</div>}
        {err && <div className="text-sm text-red-600">Error: {err}</div>}
      </div>

      {res && (
        <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-semibold">{res.flow?.name}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">converted</span>
            <button onClick={download} className="ml-auto text-xs px-3 py-1 rounded-md bg-tnh-accent text-white hover:bg-tnh-accent/90">Download flow JSON</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <S k="Activities" v={res.stats?.activities} />
            <S k="Links" v={res.stats?.links} />
            <S k="Holiday subflow" v={res.stats?.holiday ? 'yes' : 'no'} />
            <S k="Manual review" v={res.stats?.manual} />
          </div>
          {res.manual_review?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">MANUAL_REVIEW</div>
              <ul className="text-sm list-disc pl-5 space-y-0.5 text-gray-600 dark:text-gray-300">
                {res.manual_review.map((m: string, i: number) => <li key={i}>{m}</li>)}
              </ul>
              <p className="text-[11px] text-gray-400 mt-2">Finish branch ports (holiday/working-hours, DTMF keys) in WxCC Flow Designer; the flow runs through the freshener before publish.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function S({ k, v }: { k: string; v: any }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-tnh-border dark:border-tnh-border-dark p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{k}</div>
      <div className="text-base font-semibold">{v ?? '—'}</div>
    </div>
  );
}
